const configuredJitExpressFacilitiesCardId = Number(
  import.meta.env.VITE_OPEN_WORK_MAP_JIT_FACILITIES_CARD_ID ?? 9962,
);
const configuredOpenWorkMapWorkCardId = Number(
  import.meta.env.VITE_OPEN_WORK_MAP_WORK_CARD_ID ?? 9970,
);

export const OPEN_WORK_MAP_WORK_CARD_ID =
  Number.isInteger(configuredOpenWorkMapWorkCardId) && configuredOpenWorkMapWorkCardId > 0
    ? configuredOpenWorkMapWorkCardId
    : 9970;

export const OPEN_WORK_MAP_JIT_FACILITIES_CARD_ID =
  Number.isInteger(configuredJitExpressFacilitiesCardId) && configuredJitExpressFacilitiesCardId > 0
    ? configuredJitExpressFacilitiesCardId
    : 9962;

/** Metabase Cube card IDs. Merch-dashboard cards live in Bot Playground (collection 223). */
export const CUBE_CARD_IDS = {
  /* Hub Open Work Map read contracts in the shared Ops / Hub Open Work Map
     collection. Preserve IDs and template-tag contracts when editing. */
  openWorkMapWork: OPEN_WORK_MAP_WORK_CARD_ID,
  openWorkMapActiveRiders: 9956,
  openWorkMapActiveFacilities: 9957,
  /* Configured JIT B2B2C Express partner facilities. The environment override
     remains available for isolated card-contract review. */
  openWorkMapJitExpressFacilities: OPEN_WORK_MAP_JIT_FACILITIES_CARD_ID,
  /* One-rider/one-business-day ClickHouse evidence. The card returns a bounded,
     downsampled trail and explicit rider actions; it never returns raw event JSON. */
  riderDayEvidence: 9985,
  /* Rider Day distance remains separate from the presentation trail: Postgres
     supplies authoritative shifts; ClickHouse calculates one aggregate row per
     exact shift from raw, shift-tagged device evidence. */
  riderDayShiftContext: 10001,
  riderDayDistanceEvidence: 10002,
  /* Existing */
  brandWiseMTOPendency: 3000,
  /* 2026-07-16 inwards DC Qty fix: 9239/9240/9241 supersede 6529/6532/6534.
     The old cards counted every stg_wms_delivery_challan_item row, including
     enabled=false rows — superseded DC drafts that the warehouse service
     itself filters out — which double-counted DC Qty (e.g. DC 21102 read 59
     against a true 30). Old cards left intact for prod compat. */
  inwardTrackerBrandSummary: 9239 /* was 6529 */,
  inwardTrackerItems: 9240 /* was 6532 */,
  inwardTrackerStoreBreakdown: 9241 /* was 6534 */,

  /* Merch dashboard tabs (created 2026-05-06) */
  /* 2026-07-22 date-exact flows: 9370/9371/9372 supersede 6963/6973/8126.
     The old cards filtered by toStartOfWeek(), returning whole ISO weeks
     overlapping date_start..date_end — a mid-week range boundary overstated
     flows (Jul 1–31 pulled Jun 29–30 too: +32L Net GMV vs card 437). The new
     cards keep week-grain mart rows for snapshots/history columns but override
     flow columns with date-clipped recomputes from mart_complete_ledger /
     mart_order, and add unmatched-ledger rows (e.g. COYU gift vouchers, whose
     NULL ledger portfolio never joined the mart) so totals reconcile with the
     ledger to the rupee. Old cards left intact for prod compat. */
  saleStockBrandSummary: 9370 /* was 6963 */,
  /* 2026-06-10 merch dashboard refresh: 8126 supersedes 6964 (adds live_*
     inventory fields from mart_latest_warehouse_inventory), 8129 supersedes
     6977 (adds mtc_items/mtc_gmv), 8130 supersedes 7434 (adds not-live-but-
     in-stock counts). Old cards left intact for prod compat — delete once the
     new admin is stable. 8127/8128 are brand-new (date-grain brand MTD; All
     Styles Sold store split + issue stickers). */
  /* 2026-08-05: 9651 supersedes 9372 — keeps date-exact flows, adds
     inventory-only warehouse rows (excluding Dwarka Warehouse) so Store × Brand
     live stock totals and breakdowns can include warehouse inventory without
     inventing GMV / qty / Rev-Hanger for those rows. */
  storeBrandSummary: 9651 /* was 9372, before that 8126 / 6964 */,
  brandMtdNetGmv: 8127,
  /* 2026-07-23: 9397 supersedes 8128 — identical query minus the Dwarka
     Warehouse inventory rows (per ask: no Dwarka in merch-report inventory).
     8128 left intact for prod compat. */
  allStylesSoldStoreSplit: 9397 /* was 8128 */,
  bestsellersTopStyles: 6965,
  bestsellersSizeDemand: 6966,
  aiTagWeeklySummary: 6967,
  coyuSearchesTopQueries: 6968,
  stockTransferBrandSummary: 6969,
  stockTransferGpoList: 6970,
  /* MTO Health redesign — cards 7437-7444 now expose MTO/MTC/JIT-B2B three-clock
     semantics. The legacy 6971/6972 Admin cards were removed after the redesign
     stabilized in production. */
  mtoOrdersSummary: 7437,
  mtoLiveOrders: 7438,
  mtoWeeklyTrend: 7439,
  mtoDelayHistogram: 7440,
  mtoDelayDetailOrders: 7443,
  mtoLiveOrdersDetail: 7444,
  /* MTOC merch-head accountability pack. Uses brand_manager as the current
     merch-head proxy and mirrors Dashboard 195 Brand Health Score semantics. */
  mtoMerchHeadWeeklyLeaderboard: 7758,
  mtoMerchHeadWeeklyTrend: 7759,
  mtoMerchHeadBrandContribution: 7760,
  mtoMerchHeadIssueBreakdown: 7761,
  mtoMerchHeadRawIssueLines: 7762,
  /* Admin-only selected-window merch accountability aggregates. Dashboard 195 keeps
     weekly cards 7758-7762 for history/trend; Admin uses these date-picker-aligned
     summaries for current actioning. */
  mtoMerchHeadSelectedWindowComparison: 7763,
  mtoMerchPortfolioSelectedWindowComparison: 7764,
  /* JIT B2B2C vendor packing SLA cards over mart_jit_b2b2c_package_lifecycle (2026-05-19). */
  jitB2B2CVendorPackingSlaSummary: 7497,
  jitB2B2CVendorPackingSlaWeekly: 7498,
  jitB2B2CVendorPackingSlaByBrand: 7499,
  jitB2B2CVendorPackingSlaRaw: 7501,
  catalogCreationWeeklySummary: 9371 /* was 6973 */,
  catalogQualityBrandSummary: 6974,
  tagDetailItems: 6975,
  /* Stock Ageing rework (2026-05-13):
     Card 6976 is the legacy per-style detail card — left intact so the
     currently-deployed prod admin keeps working. The new admin uses two
     brand-new cards (constant payload + lazy detail). Both IDs are
     populated by running /tmp/apply_merch_card_updates.py and pasting
     the printed IDs here. 6976 can be deleted once the new admin is
     stable in prod. */
  stockAgeingLegacyDetail: 6976 /* DEPRECATED — keep for prod compat, delete later */,
  stockAgeingBrandBucket: 7427,
  stockAgeingWritedown: 7428,
  /* Facility × bucket payload for the Stock Overview & Ageing facility chart. */
  stockAgeingFacilityBucket: 7433,
  /* Brand-level current-state catalog styles health (live, P0/P1, % not-live). */
  catalogStylesHealth: 8130 /* was 7434 */,
  /* Monthly Merch KPIs tab (2026-06-11) — month × BM × portfolio grain. */
  monthlyMerchKpiRevenue: 8170,
  /* 2026-07-24: 9404 supersedes 8171 — same mart, brands with ≤10 live Coyu
     units excluded from size availability / 3+ sizes. 8171 left intact. */
  monthlyMerchKpiSizeAvailability: 9404 /* was 8171 */,
  monthlyMerchKpiCx: 8172,
  /* Per-BM monthly targets from the Targets editor (goalpost → Supabase →
     mesh.stg_supabase_monthly_brand_manager_targets). Powers the report's KPI
     targets, falling back to targets.ts where the mart has no value. */
  monthlyMerchKpiBrandManagerTargets: 9697,
  onlinePerformanceDaily: 8129 /* was 6977 */,
  /* Online Performance traffic section (events.raw_lk_events product page_views,
     India web + all app; source from UTM / fbclid / gclid). */
  onlineTrafficBrandSource: 10118,
  onlineTopTrafficStyles: 10119,
  onlineLiveStyles: 10120,
  /* Online add-to-carts per brand from the cart service (channel 6, WEBSITE / APP). */
  onlineAddToCarts: 10126,
  stockReconciliationFacilitySummary: 6978,
  /* Generic Envers audit-history lookup (created 2026-05-15) — parameterised
     by entity_kind (e.g. wms.vendor) and entity_id. Postgres-backed. */
  auditHistory: 7446,
  stylistLeaderboard: 7696,
  brandLeadInfo: 8815,
  /* Operations pages (2026-07-23) — Movements + Stock views. 9419/9392 take
     date_start/date_end; the stock cards are live-state, no params. */
  /* 2026-07-25: 9415 superseded 9391 — excludes DISCARDED DCs, which the
     warehouse service treats as voided (terminal state, edits rejected) but
     9391 still counted, showing phantom Pending GRN and inflating received
     qty (INDE PRET's discarded Inde_DC read 196 pending).
     2026-07-26: 9419 supersedes 9415 — pending_grn_qty is now also 0 for
     CLOSED/GRN_PROCESSED DCs. Terminal statuses mean GRN work is done; the
     received-vs-GRN gap on them is a shortfall or a Unicom PO-code link miss
     (GRN booked under a '-1' suffixed PO, e.g. KD/CH/26-27/00297 read 32
     pending despite GRN G0921 being QC_COMPLETE). Old cards left intact. */
  opsInwardsDcSummary: 10109 /* + uploaded_at; was 10107 + cataloguer_name; was 10104 + stock_received_at; was 10100 (+ status_since), was 10094, 9419 (correction = whole received qty), 9415, 9391 */,
  opsGpoPerformance: 9392,
  opsStockByLocation: 9393,
  opsStockAgeing: 9394,
  opsStockVelocity: 9395,
  opsStockRecon: 9396,
  /* Current pending-for-GRN DC backlog (no date window), with days pending.
     2026-07-26: 9420 supersedes 9398 — the received>GRNed branch now also
     excludes CLOSED/GRN_PROCESSED (93 closed DCs were polluting the funnel
     via the same suffixed-PO GRN link miss). 9398 left intact. */
  /* Per-DC stage timeline: status transitions from stg_wms_delivery_challan_aud
     timed by stg_scm_revision.revision_at, plus the arrived_on milestone. */
  opsDcTimeline: 10102 /* was 10096 (1970 dates while stg_scm_revision lags) */,
  opsPendingDcs: 10108 /* + cataloguer_name; was 10106 + stock_received_at timestamp; was 10103 (true stock received date), was 10101 (+ status_since), 10095, 9420, 9398 */,
  /* Per brand × facility incoming/outgoing in-transit + unicom bad inventory. */
  opsStockLocationExtras: 9400,
  /* Style-grain age + 28d sales for the Ageing × Velocity 2x2 + replen view. */
  opsActionMatrix: 9402,
  /* SKU-grain age + 28d sales — lazy-loaded for SKU/qty-level exports. */
  opsSkuExport: 9403,
} as const;
